import { datadogRum } from "@datadog/browser-rum";
import { MaybeJwtUser } from "shared-ui/utils/tokens";
import { getOrgUserDetails } from "../pages/Auth/OrgUtils";
import { ReportingOrgFragment, ReportingUserFragment } from "../types";
import { ReportingEvent } from "./events";
import { initDatadogRum, isTestUserEmail } from "@notemeal/reporting-datadog-rum";
import { environment } from "../environment";
import { cioAnalytics } from "./customerio";
const gainsightPxTag = `
(function(n,t,a,e,co){var i="aptrinsic";n[i]=n[i]||function(){
    (n[i].q=n[i].q||[]).push(arguments)},n[i].p=e;n[i].c=co;
  var r=t.createElement("script");r.async=!0,r.src=a+"?a="+e;
  var c=t.getElementsByTagName("script")[0];c.parentNode.insertBefore(r,c)
})(window,document,"https://web-sdk.aptrinsic.com/api/aptrinsic.js","${environment.VITE_GAINSIGHT_PX_KEY_TAG}");
`;

// Axle integration todo: update IDs per this example: https://www.notion.so/teamworksoss/Teamworks-Gainsight-Implementation-Guide-12e6e70bf842495084e4e11f893cec1f?pvs=4
const isTactical = environment.VITE_ENV_TYPE === "tactical";

export const initReporting = async () => {
  if (!isTactical) {
    const gainsightPxScript = document.createElement("script");
    gainsightPxScript.textContent = gainsightPxTag;
    document.body.appendChild(gainsightPxScript);
  }

  if (environment.VITE_DATADOG_RUM_ENABLED === "true") {
    initDatadogRum({
      applicationId: environment.VITE_DATADOG_APPLICATION_ID,
      clientToken: environment.VITE_DATADOG_CLIENT_TOKEN,
      site: environment.VITE_DATADOG_SITE,
      service: "notemeal-web",
      version: environment.VITE_COMMIT_HASH,
      commit: environment.VITE_COMMIT_HASH,
      datadogEnv: environment.VITE_DATADOG_ENV,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: isTactical ? "mask" : "allow",
      allowedTracingUrls: [
        {
          match: (url: string) => url.startsWith(`${environment.VITE_URL_SCHEME}${environment.VITE_SERVER_DOMAIN}/`),
          propagatorTypes: ["datadog"],
        },
      ],
    });
  }
};

export const clearReportingUser = () => {
  if (!isTactical) {
    window["aptrinsic"]("reset");
    cioAnalytics?.reset();
  }
  datadogRum.setUser({});
};

export const setReportingUser = (
  jwtUser: MaybeJwtUser,
  reportingUser: ReportingUserFragment,
  reportingOrg: ReportingOrgFragment | null,
  forStaff: boolean
) => {
  const { userId: id, userName, isStaff, orgName } = getOrgUserDetails(jwtUser);
  const roles = jwtUser?.orgMembership?.roles || [];
  const twnOrgId = forStaff ? "staff" : reportingOrg?.id;

  if (isTactical) {
    datadogRum.setUser({ id, isStaff });
  } else {
    datadogRum.setUser({
      id,
      name: userName,
      isStaff,
      orgName,
    });
  }

  if (isTactical) {
    return;
  }

  // Ignore @sink.sendgrid.net and qawolf.email email addresses from logging
  if (isTestUserEmail(reportingUser.email)) {
    return;
  }

  // Customer.io identification
  const cioId = reportingUser.orgMembershipAxleProfileDocumentId ?? `twn_${reportingUser.id}_${twnOrgId}`;
  cioAnalytics?.identify(cioId, {
    userid: reportingUser.orgMembershipAxleProfileDocumentId,
    email: reportingUser.email,
    nutrition_territory: reportingOrg?.territoryName,
    nutrition_role: roles.sort().join(", "),
    nutrition_created_at: Math.floor(Date.parse(reportingUser.dateJoined) / 1000),
    nutrition_has_pk: reportingOrg?.performanceKitchen ? "true" : "false",
    nutrition_version: environment.VITE_COMMIT_HASH,
    nutrition_commit: environment.VITE_COMMIT_HASH,
  });

  window["aptrinsic"](
    "identify",
    {
      id: reportingUser.orgMembershipAxleProfileDocumentId ?? `twn_${reportingUser.id}_${twnOrgId}`, // prefixing ids with twn to avoid confustion with axel ids in the future
      nutritionUserId: reportingUser.id,
      email: reportingUser.email,
      firstName: reportingUser.firstName,
      lastName: reportingUser.lastName,
      nutritionUsername: jwtUser?.username,
      nutritionUserRole: roles.sort().join(", "),
      nutritionHubProfileId: jwtUser?.orgMembership?.teamworksId,
      nutritionIsActive: reportingUser.isActive,
      nutritionIsSuperuser: jwtUser?.isSuperuser,
      signUpDate: reportingUser.dateJoined,
    },
    {
      id: `twn_${twnOrgId}`, // prefixing required ids with twn to avoid confustion with axel ids in the future
      name: reportingOrg?.name,
      nutritionHubOrgId: reportingOrg?.teamworksId,
      nutritionOrgTerritory: reportingOrg?.territoryName,
      twnHasPK: reportingOrg?.performanceKitchen,
    }
  );
};

export const trackEvent = <E extends keyof ReportingEvent>(eventName: E, payload: ReportingEvent[E]) => {
  if (!isTactical) {
    window["aptrinsic"]("track", eventName, payload);
  }
  datadogRum.addAction(eventName, payload);
};
